import { nomoCharacters } from '@/mocks/characters';
import { flavorDetails } from '@/mocks/flavorDetail';
import { nomoStories } from '@/mocks/stories';

export interface SeoData {
  title: string;
  description: string;
  keywords: string;
  path: string;
  image?: string;
  ogType: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_TITLE = 'NOMOISM 官方网站｜汲取美食灵感，塑造东方潮玩。';
const SITE_DESCRIPTION =
  'NOMOISM 从中国美食出发，以「一味 × 一境 × 一愿」的创作公式，把中国味道、东方风土与当代人的愿望，变成可以看见、触摸与收藏的东方潮玩 IP。';
const SITE_KEYWORDS = 'NOMOISM,东方潮玩,国潮IP,鱼豆豆,潮玩,盲盒,手办,潮玩品牌';
const HOME_IMAGE =
  'https://static.readdy.ai/image/f07c49612aa26b21f7c4157143a2b686/59de4b10145b62f58a9ace809f439b65.jpeg?ogv=sapwqg';

export function siteOrigin(): string {
  const configured = import.meta.env.VITE_SITE_URL as string | undefined;
  if (configured) return configured.replace(/\/+$/, '');
  if (typeof window !== 'undefined' && window.location.origin) {
    return window.location.origin;
  }
  return '';
}

function normalizePath(pathname: string): string {
  let path = pathname || '/';
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
  if (!path.startsWith('/')) path = `/${path}`;
  return path;
}

export function resolveSeo(pathname: string): SeoData | null {
  const path = normalizePath(pathname);
  const origin = siteOrigin();
  const url = `${origin}${path}`;

  if (path === '/') {
    return {
      title: SITE_TITLE,
      description: SITE_DESCRIPTION,
      keywords: SITE_KEYWORDS,
      path: '/',
      image: HOME_IMAGE,
      ogType: 'website',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'NOMOISM',
        url,
        description: SITE_DESCRIPTION,
        logo: 'https://static.readdy.ai/image/f07c49612aa26b21f7c4157143a2b686/b8081839464906d4b2c5254107a25f5f.jpeg',
      },
    };
  }

  if (path === '/characters') {
    return {
      title: 'NOMOISM IP 角色｜一味一境一愿 · 东方潮玩',
      description:
        'NOMOISM 的全部 IP 角色都在这里：鱼豆豆、辣墩墩、面长长、鸭珑珑、虾饺饺。每一个都是一味、一境、一愿，从中国美食里长出来的东方潮玩。',
      keywords: 'NOMOISM,IP角色,东方潮玩,鱼豆豆,辣墩墩,面长长,鸭珑珑,虾饺饺,国潮IP',
      path: '/characters',
      image: nomoCharacters[0]?.image,
      ogType: 'website',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'NOMOISM IP 角色',
        url,
        description: 'NOMOISM 东方潮玩 IP 角色总览。',
      },
    };
  }

  if (path === '/stories') {
    return {
      title: 'NOMOISM 品牌故事｜一味一境一愿 · 内容库',
      description:
        'NOMOISM 品牌故事内容库：从一道菜，到一个地方，到一群人，最后到一个角色。FOOD / PLACE / PEOPLE / CHARACTER / PLAY。',
      keywords: 'NOMOISM,品牌故事,东方潮玩,食物故事,地方故事,人的故事,角色故事,潮玩故事',
      path: '/stories',
      image: nomoStories[0]?.image,
      ogType: 'website',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'NOMOISM 品牌故事',
        url,
        description: 'NOMOISM 品牌故事内容型资产库。',
      },
    };
  }

  const characterMatch = path.match(/^\/character\/([^/]+)$/);
  if (characterMatch) {
    const character = nomoCharacters.find((item) => item.id === characterMatch[1]);
    if (character) {
      return {
        title: `${character.name}（${character.enName}）｜NOMOISM 东方潮玩 IP`,
        description: `${character.name}是 NOMOISM 的东方潮玩 IP 角色，一味是${character.flavor}，一境是${character.place}，一愿是${character.wish}。${character.origin}`,
        keywords: `NOMOISM,${character.name},东方潮玩,${character.flavor},${character.place},国潮IP`,
        path,
        image: character.image,
        ogType: 'website',
      };
    }
    return null;
  }

  const flavorMatch = path.match(/^\/flavor\/([^/]+)$/);
  if (flavorMatch) {
    const flavor = flavorDetails.find((item) => item.id === flavorMatch[1]);
    if (flavor) {
      return {
        title: `${flavor.dish}｜${flavor.city} · NOMOISM 一味`,
        description: `${flavor.dish}（${flavor.dishEn}）：${flavor.intro}`,
        keywords: `NOMOISM,${flavor.dish},${flavor.city},中国味道,一味一境一愿,${flavor.wish}`,
        path,
        image: flavor.image,
        ogType: 'website',
      };
    }
    return null;
  }

  const storyMatch = path.match(/^\/stories\/([^/]+)$/);
  if (storyMatch) {
    const story = nomoStories.find((item) => item.id === storyMatch[1]);
    if (story) {
      return {
        title: `${story.title}｜NOMOISM 品牌故事`,
        description: story.excerpt,
        keywords: `NOMOISM,品牌故事,${story.categoryZh},${story.category},东方潮玩`,
        path,
        image: story.image,
        ogType: 'article',
        jsonLd: {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: story.title,
          description: story.excerpt,
          image: story.image,
          author: { '@type': 'Organization', name: 'NOMOISM' },
          publisher: { '@type': 'Organization', name: 'NOMOISM' },
          mainEntityOfPage: url,
        },
      };
    }
    return null;
  }

  return null;
}